import {
  type DiagnosticItem,
  DiagnosticStatus,
} from "@/schemas/app/diagnostic";
import type { AppUserToken } from "@/schemas/auth/session";
import { maskSensitiveValue } from "@/utils/secrets";

/**
 * Calculates the time difference between now and the expiration timestamp
 * Shows remaining time if token is valid, or time since expiration if expired
 * @param expiresAt ISO timestamp string
 * @returns Formatted string showing time information
 */
export const calculateRemainingTime = (
  expiresAt: string | undefined,
  startingPoint: Date,
): string => {
  if (!expiresAt) return "No expiration time";

  try {
    // Parse the expiration timestamp
    const expirationDate = new Date(expiresAt);

    // Calculate difference in milliseconds (absolute value)
    const diffMs = Math.abs(expirationDate.getTime() - startingPoint.getTime());

    // Convert to more readable units
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    // Format the output based on whether it's expired or not
    if (expirationDate <= startingPoint) {
      // Token is expired - show how long ago it expired
      if (diffDays > 0) {
        return `Expired ${diffDays} days, ${diffHours} hours, ${diffMinutes} minutes ago`;
      } else if (diffHours > 0) {
        return `Expired ${diffHours} hours, ${diffMinutes} minutes ago`;
      } else {
        return `Expired ${diffMinutes} minutes ago`;
      }
    } else {
      // Token is still valid - show remaining time
      if (diffDays > 0) {
        return `${diffDays} days, ${diffHours} hours, ${diffMinutes} minutes remaining`;
      } else if (diffHours > 0) {
        return `${diffHours} hours, ${diffMinutes} minutes remaining`;
      } else {
        return `${diffMinutes} minutes remaining`;
      }
    }
  } catch (error) {
    return "Invalid expiration format";
  }
};

const fetchSessionInfo = async (
  token: AppUserToken | null,
): Promise<DiagnosticItem[]> => {
  const now = new Date();
  const remainingTime = calculateRemainingTime(token?.expiresAt, now);

  return [
    {
      id: "token",
      name: "Session Token",
      value: token?.accessToken
        ? maskSensitiveValue(token.accessToken)
        : "No token",
      status: token !== null ? DiagnosticStatus.OK : DiagnosticStatus.ERROR,
    },
    {
      id: "refreshToken",
      name: "Refresh Token",
      value: token?.refreshToken
        ? maskSensitiveValue(token.refreshToken)
        : "No refresh token",
      status: DiagnosticStatus.OK,
    },
    {
      id: "expiredTime",
      name: "Expires At",
      value: token?.expiresAt ? `${token.expiresAt}` : "No expiration time",
      status: DiagnosticStatus.OK,
    },
    {
      id: "remainingTime",
      name: "Remaining Time",
      value: remainingTime,
      status: DiagnosticStatus.OK,
    },
  ];
};

export default fetchSessionInfo;
